import { WebsocketProvider } from '@superthread-com/y-websocket';
import { Ref, onUnmounted, ref, unref } from 'vue';
import { Awareness } from 'y-protocols/awareness';
import { Doc } from 'yjs';
import { MaybeRef, maybeUnref, refValueIsDefined } from '../../../utilities/maybeRef';
import { fromEvent, map, filter, Subscription, bufferTime, merge, tap } from 'rxjs';
import { Editor } from '@tiptap/vue-3';

/**
 * Sets the class of the flag to the 'up' variant,
 * which makes it visible. Provide the yjs client id
 * in order to reveal the respective user's flag.
 * @param yjsId
 * @param flagVisibility
 */
const setCursorFlag = (yjsId: number, flagVisibility: 'up' | 'down', userName: string) => {
  const cursor = document.getElementById(`yjs-cursor-${yjsId}`);
  if (cursor) {
    if (cursor.dataset.username !== userName) {
      cursor.setAttribute('data-username', userName);
    }
    if (flagVisibility === 'up') {
      cursor.classList.add('ProseMirror-yjs-cursor-up');
    } else {
      cursor.classList.remove('ProseMirror-yjs-cursor-up');
    }
  }
};

/**
 * Updates the local state of the flag visbility.
 * @param awareness
 * @param flagVisibility
 */
const updateAwareness = (awareness: Ref<Awareness | undefined>, flagVisibility: 'up' | 'down') => {
  awareness.value?.setLocalStateField?.('flag', flagVisibility);
  /**
   * This redundancy is required in order to provide
   * the flag status as part of the user data.
   *
   * The user state field won't be used for updates
   * (since it can receive more frequent updates than
   * needed, hence making it difficult to distinguish
   * which update should be considered for flag changes).
   *
   * The user state field is used, however, by the cursor
   * builder, which draws the actual DOM elements.
   */
  awareness.value?.setLocalStateField?.('user', {
    ...(awareness.value?.getLocalState()?.user || {}),
    flag: flagVisibility,
  });
};

const attachFlagUpdateSubscription = (awareness: Ref<Awareness>) => {
  /**
   * Take all update events.
   */
  return fromEvent(awareness.value, 'update')
    .pipe(
      /**
       * Map to object of updated ids and awareness object.
       * Awareness object may not be always defined (when local changes occur).
       */
      map((updatePayload: any) => ({
        updated: [...updatePayload[0].updated],
        awareness: updatePayload[1]?.awareness,
      })),
      /**
       * Only select events where awareness is defined.
       */
      filter((ev) => typeof ev.awareness === 'object')
    )
    .subscribe(({ updated, awareness }) => {
      /**
       * For each updated id check the flag state.
       * Updates transmitted through this event do not
       * provide any PATCH or DIFF, nor the full state.
       *
       * So, it must be inspected from the locally stored
       * and synced awareness object.
       */
      updated.forEach((id) => {
        const awarenessState = awareness?.states?.get?.(id);
        if (awarenessState) {
          setCursorFlag(id, awarenessState?.flag ?? 'down', awarenessState?.user?.name ?? '');
        }
      });
    });
};

const attachFlagInputSubscription = (
  awareness: Ref<Awareness | undefined>,
  editor: Editor,
  timeout: number
) => {
  /**
   * Takeall input and click events from the editor DOM.
   */
  return merge(fromEvent(editor.view.dom, 'input'), fromEvent(editor.view.dom, 'click'))
    .pipe(
      /**
       * If such an event occurs, immediately
       * raise the flag. This doesn't consume the event,
       * but instead passes it further.
       */
      tap(() => updateAwareness(awareness, 'up')),
      /**
       * Buffer the event. Each `timeout` miliseconds,
       * an event is emitted with all the events
       * that occured over the time period.
       */
      bufferTime(timeout)
    )
    .subscribe((event) => {
      /**
       * If over a `timeout` miliseconds time period
       * no events have been recorded, hide the flag.
       *
       * Otherwise, reveal it.
       */
      if (event?.length > 0) {
        updateAwareness(awareness, 'up');
      } else {
        updateAwareness(awareness, 'down');
      }
    });
};

export default function useAwareness(wsProviderRef?: MaybeRef<WebsocketProvider>) {
  const provider: Ref<WebsocketProvider | null> = maybeUnref(wsProviderRef);

  const awareness = ref<Awareness>();

  const updatesSubscription = ref<Subscription>();
  const inputSubscription = ref<Subscription>();
  const providerListenerHandler = ref<CallableFunction | null>();

  /**
   * Creates a new instance for awareness.
   */
  function init(ydoc: Doc) {
    awareness.value = new Awareness(ydoc);
  }

  function destroyProviderListener() {
    if (providerListenerHandler.value) {
      provider?.value?.off?.('status', providerListenerHandler.value);
      providerListenerHandler.value = null;
    }
  }

  function enableFlagUpdates(wsProvider?: WebsocketProvider) {
    if (wsProvider) {
      destroyProviderListener();
      provider.value = wsProvider;
    }

    if (provider.value === null) {
      throw new Error('Awareness requires a valid provider.');
    }

    destroyProviderListener();

    providerListenerHandler.value = (event: any) => {
      if (event.status === 'connected' && refValueIsDefined(awareness)) {
        updatesSubscription.value?.unsubscribe?.();

        updatesSubscription.value = attachFlagUpdateSubscription(awareness);
      }
    };

    provider.value.on('status', providerListenerHandler.value);
  }

  function enableFlagInput(editorRef: MaybeRef<Editor>, timeout = 1500) {
    const editor = unref(editorRef);

    inputSubscription.value?.unsubscribe?.();

    inputSubscription.value = attachFlagInputSubscription(awareness, editor, timeout);
  }

  function destroy() {
    updatesSubscription.value?.unsubscribe?.();
    inputSubscription.value?.unsubscribe?.();
    destroyProviderListener();
    unref(awareness)?.destroy();
  }

  onUnmounted(() => {
    destroy();
  });

  return {
    awareness,
    init,
    enableFlagUpdates,
    enableFlagInput,
    destroy,
  };
}
